import { FeatureTour } from "@/components/common/FeatureTour";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const features = [
    {
        title: "Tab & Note Workspace",
        description: "See your tabs and notes side by side. Switch between them instantly for a seamless workflow.",
        image: "/assets/feature_tab_and_note.png",
    },
    {
        title: "AI-Powered Notes",
        description: "Smart note editor with grammar suggestions, text enhancement, and summarization.",
        image: "/assets/feature_note.png",
    },
    {
        title: "Organize Collections",
        description: "Group your tabs and notes into collections for every project or topic.",
        image: "/assets/feature_collection.png",
    },
    {
        title: "Add Tabs Effortlessly",
        description: "Quickly add new tabs to your workspace and collections with a single click.",
        image: "/assets/feature_add_tab.png",
    },
];

const workflowSteps = [
    {
        title: "Switch Views",
        description: "Easily switch between Tabs and Notes views",
        image: "/assets/tab_light_mode.png", // TODO: Add view switching image
    },
    {
        title: "Create Collections",
        description: "Organize your tabs into meaningful collections",
        image: "/assets/tab_light_mode_add_tab.png",
    },
    {
        title: "AI-Powered Notes",
        description: "Enhance your notes with AI suggestions",
        image: "/assets/note_light_mode.png",
    },
];

const heroScreenshots = [
    "/assets/note_light_mode.png",
    "/assets/tab_light_mode.png",
];

export default function IndexPage() {
    return (
        <div className="bg-gradient-to-b from-orange-400 via-orange-100 to-white min-h-screen flex flex-col">
            {/* Animated SVG/CSS background for the entire page */}
            <div className="fixed inset-0 pointer-events-none z-0 w-full h-full">
                <svg width="100%" height="100%" className="w-full h-full animate-pulse-slow" style={{position:'absolute',top:0,left:0}}>
                    <circle cx="30%" cy="30%" r="180" fill="none" stroke="#fbb6ce" strokeWidth="2" opacity="0.2">
                        <animate attributeName="r" values="180;200;180" dur="6s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="70%" cy="60%" r="120" fill="none" stroke="#a5b4fc" strokeWidth="2" opacity="0.15">
                        <animate attributeName="r" values="120;140;120" dur="7s" repeatCount="indefinite" />
                    </circle>
                    <path d="M 0 400 Q 400 200 1200 400" stroke="#f472b6" strokeWidth="1.5" fill="none" opacity="0.12">
                        <animate attributeName="d" values="M 0 400 Q 400 200 1200 400;M 0 420 Q 400 220 1200 420;M 0 400 Q 400 200 1200 400" dur="8s" repeatCount="indefinite" />
                    </path>
                </svg>
            </div>

            {/* Header */}
            <nav className="relative z-10 w-full flex items-center justify-between px-4 py-4 md:px-20 md:py-6 bg-transparent">
                <div className="flex items-center gap-2">
                    <img src="/icons/icon.svg" alt="Logo" className="h-8 w-8" />
                    <span className="text-2xl font-bold text-gray-900 tracking-tight">Hintword</span>
                </div>
                {/* <div className="flex items-center gap-8">
                    <a href="/pricing" className="text-gray-700 hover:text-primary font-medium transition">Pricing</a>
                    <a href="/help" className="text-gray-700 hover:text-primary font-medium transition">Help Center</a>
                    <a href="/blog" className="text-gray-700 hover:text-primary font-medium transition">Blog</a>
                    <Link to="/login">
                        <button className="ml-6 px-6 py-2 rounded-full bg-primary text-white font-semibold text-base shadow hover:bg-primary/90 transition hover:scale-105">Try Hintword</button>
                    </Link>
                </div> */}
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 w-full flex flex-col md:flex-row items-center justify-between px-4 md:px-20 2xl:px-40 pb-10 md:pb-20 bg-transparent">
                <div className="flex-1 flex flex-col items-start justify-center max-w-xl md:pr-20">
                    <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 leading-tight">Stop Losing Focus. Start Creating with Hintword.</h1>
                    <p className="text-lg md:text-xl mb-8 md:mb-10 text-gray-700">Hintword brings your notes and tabs together in a single, AI-powered workspace. Capture, organize, and focus — effortlessly</p>
                    <div className="flex flex-col gap-3 w-full sm:flex-row sm:gap-4">
                        <Link to="/login" className="w-full sm:w-auto">
                            <button className="w-full sm:w-auto px-8 py-3 md:px-10 md:py-4 rounded-full bg-white text-primary font-semibold text-lg md:text-xl shadow-lg transition hover:scale-105">Try Hintword</button>
                        </Link>
                        <a
                            href="https://chromewebstore.google.com/detail/hintword/bpehddhkacikijbjhimoomlnkfjhcgng"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto px-8 py-3 md:px-10 md:py-4 rounded-full bg-primary text-white font-semibold text-lg md:text-xl shadow-lg transition hover:scale-105 flex items-center justify-center"
                            style={{ textDecoration: 'none' }}
                        >
                            Open in Chrome
                        </a>
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-center mt-8 md:mt-0 relative w-full max-w-full md:max-w-[700px] min-h-[200px] md:min-h-[700px]">
                    <FeatureTour showDescription={false} />
                </div>
            </header>

            {/* Features Section (horizontal, alternating) */}
            <section className="flex flex-col gap-16 md:gap-24 py-10 md:py-16 px-4 md:px-24 xl:px-40 bg-transparent relative z-10">
                {features.map((feature, i) => (
                    <div key={i} className={`flex flex-col md:flex-row ${i % 2 === 1 ? 'md:flex-row-reverse' : ''} items-center gap-8 md:gap-12`}>
                        <div className="w-full md:flex-1 flex items-center justify-center mb-6 md:mb-0">
                            <img src={feature.image} alt={feature.title} className="w-full max-w-xs md:max-w-md rounded-xl shadow-lg" />
                        </div>
                        <div className="w-full md:flex-1 bg-white/90 rounded-xl shadow-md p-6 md:p-8">
                            <h2 className="text-xl md:text-3xl font-bold mb-3 md:mb-4 text-gray-900">{feature.title}</h2>
                            <p className="text-base md:text-lg text-gray-700 mb-1 md:mb-2">{feature.description}</p>
                        </div>
                    </div>
                ))}
            </section>

            {/* Footer */}
            <footer className="w-full py-6 px-4 md:px-8 flex flex-col md:flex-row items-center justify-between bg-white border-t border-gray-200 mt-auto relative z-10">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <img src="/icons/icon.svg" alt="Logo" className="h-6 w-6" />
                    <span>Hintword</span>
                </div>
                <div className="flex gap-4 md:gap-6 text-gray-400 text-sm mt-4 md:mt-0">
                    {/* <a href="/help" className="hover:underline">Help Center</a>
                    <a href="/feedback" className="hover:underline">Feedback</a>
                    <a href="/pricing" className="hover:underline">Pricing</a> */}
                    <a href="/terms" className="hover:underline">Terms</a>
                    <a href="/privacy" className="hover:underline">Privacy</a>
                </div>
            </footer>
        </div>
    );
} 